<template>
  <div class="schedule container">
    <div class="loader-wrapper" v-if="loading">
      <AlLoading size="lg" message="Retrieving Schedule" />
    </div>
    <div class="action-row">
      <div class="action-left">
        <AlButton
          class="fa fa-search-plus fa-lg"
          size="sm"
          variant="secondary"
          is-only-icon
          @click="zoomIn"
        ></AlButton>
        <AlButton
          class="fa fa-search-minus fa-lg"
          size="sm"
          variant="secondary"
          is-only-icon
          @click="zoomOut"
        ></AlButton>
        <AlButton
          class="fal fa-calendar-alt fa-lg"
          size="sm"
          variant="secondary"
          is-only-icon
          @click="toggleView"
        ></AlButton>
      </div>
      <div class="action-middle">
        <h2>{{ dateRangeString }}</h2>
      </div>
      <div class="action-right">
        <AlButton
          class="fal fa-chevron-left fa-lg"
          size="sm"
          variant="secondary"
          is-only-icon
          @click="goToPreviousWeek"
          v-if="showScrollButtons"
        ></AlButton>
        <AlButton
          class="fal fa-chevron-right fa-lg"
          size="sm"
          variant="secondary"
          is-only-icon
          @click="goToNextWeek"
          v-if="showScrollButtons"
        ></AlButton>
      </div>
    </div>
    <div ref="SchedulerComponent"></div>
  </div>
</template>

<script>
import "@agyletime/dhtmlx-scheduler-commercial";
import "@agyletime/dhtmlx-scheduler-commercial/codebase/ext/dhtmlxscheduler_collision";
import "@agyletime/dhtmlx-scheduler-commercial/codebase/ext/dhtmlxscheduler_timeline";
import "@agyletime/dhtmlx-scheduler-commercial/codebase/ext/dhtmlxscheduler_treetimeline";
import "@agyletime/dhtmlx-scheduler-commercial/codebase/ext/dhtmlxscheduler_daytimeline";
import "@agyletime/dhtmlx-scheduler-commercial/codebase/ext/dhtmlxscheduler_readonly";

import { init, timelineFormat } from "./Scheduler";

import { AlLoading, Al2Button as AlButton } from "@playvox/almendra-components";
import {
  CapacityPlannerMonthlyView,
  User,
  debounce,
  RosterConfiguration,
  Translator,
} from "@agyletime/wfm-vue-common";
import {
  addDays,
  addHours,
  differenceInDays,
  eachDayOfInterval,
  format,
  isAfter,
  isBefore,
} from "date-fns";
import { getBestTextColourForColour, hexToRgb } from "../../utils/colour";
import { sortBy, prop } from "ramda";
import sanitizeHtml from "sanitize-html";
const sanitizeOptions = {
  allowedTags: [],
  allowedAttributes: {},
};
// const scheduler = window.scheduler;

export default {
  name: "ScheduleComponent",
  props: {
    configuration: {
      type: Object,
      required: true,
    },
    workloadTasks: {
      type: Map,
      required: true,
    },
  },
  mixins: [Translator],
  components: {
    AlLoading,
    AlButton,
  },
  data() {
    const date = new Date(this.configuration.startDate);
    return {
      date,
      events: [],
      eventsMap: new Map(),
      users: new Map(),
      teams: new Map(),
      dates: new Set(),
      zoom: 70,
      loading: true,
      view: null,
      dateRangeString: "",
    };
  },
  unmounted() {
    this.destroyScheduler();
  },
  async mounted() {
    await this.preloadUsers();
    this.addDataForDateRange(this.date, addDays(this.date, 21));
    this.createScheduler();
  },
  computed: {
    showScrollButtons() {
      return this.viewName === "timeline-hours";
    },
    viewName() {
      return this.view?.name;
    },
  },
  methods: {
    destroyScheduler() {
      if (this.scheduler) {
        // this.scheduler.destructor();
        this.scheduler = null;
      }
    },
    createScheduler() {
      const sortByLabel = sortBy(prop("label"));
      /** User row without teams */
      // const users = Array.from(this.users.values()).map((user) => {
      //   return {
      //     key: user.id,
      //     label: user.name,
      //   };
      // });
      // const usersSorted = sortByLabel(users);

      const teams = Array.from(this.teams.values());
      const teamsSorted = sortByLabel(teams);

      teamsSorted.map((team) => {
        const childrenSorted = sortByLabel(team.children);
        return {
          ...team,
          children: childrenSorted,
        };
      });

      this.scheduler = init({
        ref: this.$refs.SchedulerComponent,
        defaultDate: this.date,

        // y_unit: usersSorted,

        /** Team Groupings */
        y_unit: teamsSorted,

        lightbox_sections: [
          { name: "Task", height: 50, map_to: "text", type: "textarea" },
          {
            name: "Period",
            height: 50,
            map_to: "auto",
            type: "time",
          },
        ],
        onZoomOut: this.zoomOut,
        onZoomIn: this.zoomIn,
        onSwitchView: this.toggleView,
        days:
          differenceInDays(
            this.configuration.endDate,
            this.configuration.startDate
          ) + 1,
        view: this.scheduler?.getView()?.name || "timeline-hours",
      });

      // Enable swapping between week and month view
      this.scheduler.attachEvent("onViewChange", (new_mode, new_date) => {
        this.date = new_date;
        this.view = this.scheduler.getView();

        const minDate = this.scheduler.getState().min_date;
        const maxDate = addDays(this.scheduler.getState().max_date, -1);
        this.dateRangeString = `${this.getValueInLocale(
          minDate
        )} - ${this.getValueInLocale(maxDate)}`;

        if (new_mode === "timeline-days") {
          // Fetch extra data for week view
          const start = addDays(new_date, -7);
          const end = addDays(new_date, 21);
          this.addDataForDateRange(start, end, true);
        } else {
          this.addDataForDateRange(new_date, addDays(new_date, 7), false);
        }
      });

      // * Special handling for the month view scroll to load data on the fly
      const timelineDays = this.scheduler.getView("timeline-days");
      const debouncedScroller = debounce((xPos) => {
        // Use scroll position to find last visible date
        const currentPositionEnd =
          this.$refs.SchedulerComponent.clientWidth + xPos;
        const endDate = this.scheduler
          .getView()
          .dateFromPos(currentPositionEnd);

        // Load visible dates plus a few more
        this.addDataForDateRange(this.date, addDays(endDate, 7), true);
      }, 100);
      timelineDays.attachEvent("onScroll", debouncedScroller);

      this.scheduler.attachEvent("onClick", (id) => {
        if (id) {
          this.scheduler.showLightbox(id);
        }
      });

      this.updateZoom();
    },
    async addDataForDateRange(startDate, endDate, silent = false) {
      // Only load data within allowed date ranges
      if (startDate < this.configuration.startDate) {
        startDate = this.configuration.startDate;
      }
      if (endDate > this.configuration.endDate) {
        endDate = this.configuration.endDate;
      }

      // Find any date that has not yet been loaded
      const allDates = eachDayOfInterval({
        start: startDate,
        end: endDate,
      }).filter((day) => !this.dates.has(format(day, "yyyy-MM-dd")));

      if (allDates.length === 0) {
        return;
      }
      this.loading = !silent;

      // Remember which dates have already been loaded
      allDates.forEach((day) => this.dates.add(format(day, "yyyy-MM-dd")));

      const result = await CapacityPlannerMonthlyView.getHourlyCapacityPeriod(
        this.configuration._id,
        allDates[0],
        addDays(allDates[allDates.length - 1], 1)
      );

      // Merge any continuous task into a single one
      const merged = this.mergeUserTasks(result);

      this.eventsMap = new Map([...this.eventsMap, ...merged]);

      this.events = Array.from(this.eventsMap.values());

      if (this.zoom <= 30) {
        this.date = this.configuration.startDate;
      }
      this.scheduler.parse(this.events, "json");
      this.loading = false;
    },
    async preloadUsers() {
      const rosterConfigs = await RosterConfiguration.getRosterConfigurations({
        fields: "_id,teams,name",
      });
      const selectedConfigs = rosterConfigs.filter((rc) => {
        return this.configuration.rosterConfigurationId.find(
          (rcid) => rcid._id == rc._id
        );
      });
      const teams = selectedConfigs.flatMap((c) => c.teams).map((t) => t.id);
      const uniqueTeams = new Set(teams);

      const users = await User.getUsers({
        active: "all",
        teamIds: Array.from(uniqueTeams),
        fields: `id=true&fields=name=true&fields=firstName=true&fields=lastName=true&fields=team=true`,
        pageLimit: 10000,
      });

      users.forEach((u) => {
        this.users.set(u.id.toString(), u);
      });

      this.configuration.staffingAdjustments?.forEach((adj) => {
        for (let i = 0; i < adj.hires; i++) {
          const id = `adj_${adj._id}_${i}`;
          this.users.set(id, {
            id,
            name: `Staffing - ${adj.template.name} - ${adj.team.name}`,
            team: adj.team,
          });
        }
      });

      Array.from(this.users.values()).forEach((u) => {
        if (!this.teams.has(u.team.id)) {
          this.teams.set(u.team.id, {
            ...u.team,
            children: [],
            key: u.team.id,
            label: sanitizeHtml(u.team.name, sanitizeOptions),
          });
        }
        const team = this.teams.get(u.team.id);
        team.children.push({
          key: u.id,
          label: sanitizeHtml(u.name, sanitizeOptions),
        });
        this.teams.set(u.team.id, team);
      });
    },
    zoomOut() {
      if (this.zoom < 70) {
        return;
      }
      this.zoom -= 20;
      this.updateZoom();
    },
    zoomIn() {
      if (this.zoom > 100) {
        return;
      }
      this.zoom += 20;
      this.updateZoom();
    },
    updateZoom() {
      const timeline = this.scheduler.getView();
      timeline.column_width = this.zoom;
      this.scheduler.setCurrentView();
    },
    toggleView() {
      const currentViewName = this.scheduler.getView().name;
      this.zoom = 70;

      switch (currentViewName) {
        case "timeline-hours":
          this.date = this.configuration.startDate;
          this.scheduler.getView("timeline-days").column_width = this.zoom;
          this.scheduler.setCurrentView(this.date, "timeline-days");
          break;
        default:
          this.scheduler.getView("timeline-hours").column_width = this.zoom;
          this.scheduler.setCurrentView(this.date, "timeline-hours");
      }
    },
    goToNextWeek() {
      const newDate = addDays(this.date, 7);
      if (isAfter(newDate, this.configuration.endDate)) {
        this.date = addDays(this.configuration.endDate, -7);
      } else {
        this.date = newDate;
      }
      this.scheduler.setCurrentView(this.date);
    },
    goToPreviousWeek() {
      const newDate = addDays(this.date, -7);
      if (isBefore(newDate, this.configuration.startDate)) {
        this.date = this.configuration.startDate;
      } else {
        this.date = newDate;
      }
      this.scheduler.setCurrentView(this.date);
    },
    mergeUserTasks(result) {
      const tasksByUser = new Map();
      const merged = new Map();

      // Map each task to its user
      result.forEach((hour) => {
        hour.workloadTasks.forEach((workloadTask) => {
          workloadTask.userIds.forEach((userId) => {
            const current = tasksByUser.has(userId)
              ? tasksByUser.get(userId)
              : [];

            const id = `${userId}-${format(
              hour.date,
              "yyyy-MM-dd'T'hh:mm:ss"
            )}`;
            const task = this.workloadTasks.get(workloadTask.workloadTaskId);
            current.push({
              id,
              text: sanitizeHtml(task.name, sanitizeOptions),
              start_date: format(hour.date, timelineFormat),
              end_date: format(addHours(hour.date, 1), timelineFormat),
              userId,
              color: task.colour ? `#${task.colour}` : undefined,
              textColor: `rgb(${getBestTextColourForColour(
                ...hexToRgb(task.colour)
              )})`,
              workloadTaskId: workloadTask.workloadTaskId,
            });

            tasksByUser.set(userId, current);
          });
        });
      });

      // For all tasks per user, merge it to its previous task if you can
      for (const userTasks of tasksByUser.values()) {
        let merging = null;

        for (let i = userTasks.length - 1; i >= 0; i--) {
          const current = userTasks[i];
          const next = userTasks[i - 1];

          if (
            // true == true || // Disable Merge
            i === 0 ||
            current.start_date !== next?.end_date ||
            current.workloadTaskId !== next?.workloadTaskId
          ) {
            // Apply Merge
            let res = { ...current };
            if (merging) {
              res.end_date = merging.end_date;
              merging = null;
            }
            merged.set(res.id, res);
          } else {
            // to be merged
            if (!merging) {
              merging = {
                ...current,
              };
            }
          }
        }
      }

      return merged;
    },
  },
};
</script>

<style>
@import "~/node_modules/@agyletime/dhtmlx-scheduler-commercial/codebase/dhtmlxscheduler_material.css";

.dhx_cal_container {
  height: 80vh;
}

.dhx_cal_navline {
  height: 0 !important;
}

.left-container {
  overflow: hidden;
  position: relative;
  height: 99vh;
  display: inline-block;
  width: 80%;
}

.dhx_matrix_scell.folder {
  display: flex;
}

.dhx_matrix_scell.folder .dhx_scell_expand {
  display: none;
}

.dhx_matrix_scell.folder::after {
  font-family: "Font Awesome 5 Pro";
  font-weight: 900;
  content: "\f054";
}

.dhx_matrix_scell.folder.opened .dhx_scell_expand::after {
  display: none;
}
.dhx_matrix_scell.folder.opened::after {
  font-family: "Font Awesome 5 Pro";
  font-weight: 900;
  content: "\f078";
}

.dhx_data_table.folder .dhx_matrix_cell,
.dhx_matrix_scell.folder {
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
}
.dhx_matrix_scell.folder .dhx_scell_name {
  color: black;
  font-weight: bold;
  margin-right: 1rem;
}
</style>

<style scoped lang="scss">
.container {
  height: 100%;
  width: 100%;
  position: relative;
  margin-bottom: 2rem;
}
.loader-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  z-index: 100;
}

.action-row {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  & > * {
    flex-shrink: 0;
  }

  .action-right {
    display: flex;
    justify-content: flex-end;
  }

  .action-middle {
    display: flex;
    justify-content: center;
  }
}
</style>
